import {
  Button,
  DrylusProvider,
  FormGroup,
  Input,
  Label,
  Margin,
  Size,
  Spinner,
  Text,
  Title,
} from '@drawbotics/react-drylus';
import { useForm } from '@drawbotics/use-form';
import { css } from '@emotion/css';
import { useAppContext, useController } from '@tokamakjs/react';
import { Fragment } from 'react';
import { AppContext } from 'src';
import { z } from 'zod';

import { OptionSeparator } from '~/components';
import { createTranslate, translate as t } from '~/utils';

import { CreateOrJoinCompanyAccountController } from './create-join-company.controller';

const tt = createTranslate('routes.create_join_company');

const styles = {
  secondTitle: css`
    margin-top: 0;
  `,
};

const joinCompanySchema = z
  .object({
    invitationCode: z.string().min(1, t('errors.field_empty')),
  })
  .required();

const JoinCompanyAccount = (props: { controller: CreateOrJoinCompanyAccountController }) => {
  const appContext = useAppContext<AppContext>();

  const form = useForm(joinCompanySchema, {
    defaultValues: {
      invitationCode: '',
    },
  });

  return (
    <Fragment>
      <Title className={styles.secondTitle} size={2}>
        {tt('join_company')}
      </Title>
      <Text>{tt('description')}</Text>

      <Margin size={{ top: Size.LARGE }}>
        <FormGroup
          label={<Label>{tt('invitation_code')}</Label>}
          input={
            <Input
              placeholder={tt('input_code_placeholder')}
              type={'text'}
              name="invitationCode"
              value={form.get}
              onChange={form.set}
              error={!!form.getError('invitationCode')}
            />
          }
        />
      </Margin>

      <Margin size={{ top: Size.DEFAULT }}>
        <Button
          disabled={props.controller.processingInvitation}
          trailing={
            props.controller.processingInvitation ? <Spinner size={Size.SMALL} inversed /> : null
          }
          color={appContext.color}
          onClick={(e) => {
            e.preventDefault();

            const validationResult = form.validateAll();

            if (validationResult.data) {
              props.controller.processInvitation(validationResult.data.invitationCode);
            }
          }}
          size={Size.LARGE}>
          {tt('join')}
        </Button>
      </Margin>
    </Fragment>
  );
};

const createCompanyAccountSchema = z
  .object({
    name: z.string().min(1, t('errors.field_empty')),
  })
  .required();

const CreateCompanyAccount = (props: { controller: CreateOrJoinCompanyAccountController }) => {
  const appContext = useAppContext<AppContext>();
  const form = useForm(createCompanyAccountSchema, { defaultValues: { name: '' } });

  return (
    <Fragment>
      <Title size={2}>{tt('create_new_company')}</Title>
      <Text>{tt('no_account')}</Text>

      <Margin size={{ top: Size.LARGE }}>
        <FormGroup
          label={<Label>{tt('company_name')}</Label>}
          input={
            <Input
              placeholder={tt('company_name_placeholder')}
              type={'text'}
              name="name"
              value={form.get}
              onChange={form.set}
              error={!!form.getError('name')}
            />
          }
        />
      </Margin>

      <Margin size={{ top: Size.DEFAULT }}>
        <Button
          color={appContext.color}
          disabled={props.controller.isSubmittingNewCompany}
          trailing={
            props.controller.isSubmittingNewCompany ? <Spinner size={Size.SMALL} inversed /> : null
          }
          onClick={(e) => {
            e.preventDefault();

            const validationResult = form.validateAll();

            if (validationResult.data) {
              props.controller.createNewCompany(validationResult.data);
            }
          }}
          size={Size.LARGE}>
          {tt('create_company')}
        </Button>
      </Margin>
    </Fragment>
  );
};

export const CreateOrJoinCompanyAccountView = () => {
  const controller = useController<CreateOrJoinCompanyAccountController>();

  if (controller.userIsLoading || (controller.invitationCode && controller.processingInvitation)) {
    return <Spinner fullSize size={Size.LARGE} />;
  } else if (controller.errorWhileLoadingUser) {
    return null;
  } else {
    return (
      <DrylusProvider>
        <CreateCompanyAccount controller={controller} />
        <Margin size={{ top: Size.LARGE, bottom: Size.LARGE }}>
          <OptionSeparator />
        </Margin>
        <JoinCompanyAccount controller={controller} />
      </DrylusProvider>
    );
  }
};
